/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */


import {
  ChangeDetectorRef,
  Component,
  EmbeddedViewRef,
  HostListener,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {Observable, of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {DATE_MAX, ProfileMode} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {CliEmpAttributes, ReportDefinition, ReportsFormData} from '../../models/models.interfaces';
import {OnboardingStatus} from '../../onboarding/onboarding.model';
import {AnyData, Data} from '../../shared/api';
import {ContractDataService} from '../../shared/contract-data/contract-data.service';
import {ContractStatus, ProfileType} from '../../shared/employees/contract/contract.interfaces';
import {Contract, mapContract} from '../../shared/employees/employees.model';
import {ModalService} from '../../shared/modal';
import {DateLocalizationPipe} from '../../shared/pipes/date-localization.pipe';
import {Team} from '../../shared/teams/teams.model';
import {TeamsService} from '../../shared/teams/teams.service';
import {User, UserService} from '../../shared/user';
import {isMobileDevice, isTablet} from '../../shared/utils/browser';
import {getInitials} from '../../shared/utils/strings';
import {UPDATE_MSS_SUBORDINATE_INFO_WORKFLOW, WorkflowV2Service} from '../../shared/workflow/workflow-v2.service';

const EMPLOYEE_COMMUNICATION = 'EMPLOYEE_COMMUNICATION';

@Component({
  selector: 'gp-my-team',
  templateUrl: 'my-team.component.html',
  styleUrls: ['my-team.component.scss']
})
export class MyTeamComponent extends Destroyable implements OnInit {
  public static ACCESS_NAME: string = 'MyTeam';

  public availableReportType = [EMPLOYEE_COMMUNICATION];
  public mssProfileType = ProfileType.MSS;
  public selectedEmployee: Data<CliEmpAttributes>;

  public selectedTeam: number = 0;
  public isLoading: boolean = false;
  public isFutureContracts = false;
  public userId: number;
  public teams: Team[] = [];
  public employees: Array<Data<CliEmpAttributes>> = [];
  public teamName: UntypedFormControl = new UntypedFormControl('', [Validators.required]);
  public activeMenu: EmbeddedViewRef<Data<CliEmpAttributes>>;

  public toggleSeeInactiveContracts: boolean = false;

  public selectedContract: Contract;
  public workflowName = UPDATE_MSS_SUBORDINATE_INFO_WORKFLOW;

  @ViewChild('employeeList', {read: ViewContainerRef})
  public employeeList: ViewContainerRef;

  @ViewChild('submenu', {static: true})
  public submenuTpl: TemplateRef<Data<CliEmpAttributes>>;
  public contracts: Array<Contract>;
  public menuItems: { communications: ReportDefinition[] } = {communications: null};
  public triggerSortByTranslationPipe = true;
  public selectedReportDefinition: ReportDefinition;
  public hiddenValuesForReportDefinition = new Map();
  public cliRegIds: number[];
  public contractId: number;
  public employeeId: number;
  public reportDefinitions: Array<ReportDefinition>;
  public selectedEmployeeFullName: string;
  public showDetailView = false;
  public isMobileViewport = false;
  public isTabletViewport = false;
  public searchTerm = '';
  public isCustomTeam = false;
  public newTeamMemberId: number;
  public profileMode = ProfileMode.MSS;
  public newOnboardingEnabled = false;
  public isSendingOnboarding = false;
  public selectedMenuEmployee: Data<CliEmpAttributes>;
  public isListPinned = false;
  public listExpanded = true;
  public displayIncomplete = false;
  public searchTermControl: UntypedFormControl = new UntypedFormControl('');


  @HostListener('window:click') outsideClick() {
    if (this.activeMenu) {
      this.activeMenu.destroy();
    }
  }

  public onListClickOutside(event: MouseEvent): void {
    if (this.isClickOnFloatingElement(event?.target as HTMLElement)) {
      return;
    }
    this.collapseList();
  }

  // Menu, modals and toasts are rendered outside the list: clicking them (or an element removed by that click,
  // like a modal "OK" button) must not be considered as leaving the list.
  private isClickOnFloatingElement(target: HTMLElement): boolean {
    if (!target) {
      return false;
    }
    return !target.isConnected || !!target.closest('.submenu, .modal, .modal-backdrop, .toast-container');
  }

  constructor(private userService: UserService,
              private translate: TranslateService,
              private modalService: ModalService,
              private viewContainer: ViewContainerRef,
              private teamsService: TeamsService,
              private contractDataService: ContractDataService,
              private dateLocalizationPipe: DateLocalizationPipe,
              private workflowV2Service: WorkflowV2Service,
              private toastrService: ToastrService,
              private cd: ChangeDetectorRef) {
    super();
    this.customPlaceholderGenerator = this.customPlaceholderGenerator.bind(this);
    this.newOnboardingEnabled = localStorage.getItem('gp_new_onboarding_enabled') === 'true';
  }

  public ngOnInit(): void {
    this.updateViewport();

    this.translate.onLangChange
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.triggerSortByTranslationPipe = !this.triggerSortByTranslationPipe;
      });

    this.searchTermControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((value: string) => {
        if (!value || value.length > 2) {
          this.listExpanded = true;
          this.filterTeam(value || '');
        }
      });

    this.loadTeams();
  }

  @HostListener('window:resize')
  public updateViewport(): void {
    this.isMobileViewport = isMobileDevice();
    this.isTabletViewport = isTablet();
    if (this.isMobileViewport && this.isListPinned) {
      this.isListPinned = false;
      this.listExpanded = !this.selectedEmployee;
    }
  }

  public closeActiveMenu() {
    if (this.activeMenu) {
      this.activeMenu.destroy();
    }
  }

  public filterTeam(searchTerm: string) {
    this.searchTerm = searchTerm;
    if (!this.isCustomTeam) {
      this.selectedEmployee = null;
      this.changeTeam(this.selectedTeam, searchTerm);
    }
  }

  public changeTeam(selectedTeam, searchTerm: string) {
    this.changeTeamObservable(selectedTeam, searchTerm).subscribe();
  }

  private changeTeamObservable(selectedTeam, searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    this.employees = [];
    this.selectedEmployee = null;
    this.showDetailView = false;
    this.listExpanded = true;
    this.isLoading = true;
    this.isFutureContracts = false;
    let teamList$: Observable<Array<Data<CliEmpAttributes>>>;

    switch (selectedTeam) {
      case 0 : {
        this.isCustomTeam = false;
        teamList$ = this.getDefaultTeam(searchTerm);
        break;
      }
      case 1 : {
        this.isCustomTeam = false;
        teamList$ = this.getIndirectSubordinates(searchTerm);
        break;
      }
      case 2 : {
        this.isCustomTeam = false;
        this.isFutureContracts = true;
        teamList$ = this.getFutureSubordinates(searchTerm);
        break;
      }
      case 3 : {
        this.isCustomTeam = false;
        teamList$ = this.getBackupSubordinates(searchTerm);
        break;
      }
      case 4 : {
        this.isCustomTeam = false;
        teamList$ = this.getAllSubordinates(searchTerm);
        break;
      }
      default : {
        this.isCustomTeam = true;
        teamList$ = this.getUserTeam(selectedTeam);
        break;
      }
    }

    localStorage.setItem('gp_last_viewed_team_id', selectedTeam.toString());

    this.selectedTeam = selectedTeam;

     return teamList$.pipe(
      tap(result => {
        this.employees = result.flatMap(employee => employee.empContracts.map(contract => {
          return {...employee, empContracts: [contract]};
        }));
      }),
      finalize(() => this.isLoading = false)
    );
  }

  public sendOnboardingV2() {
    if (this.isSendingOnboarding) {
      return;
    }
    if (this.isOnboardingInProgress(this.selectedMenuEmployee?.onboardingStatus)) {
      this.toastrService.error(this.translate.instant('Onboarding.employee_already_onboarded'));
      return;
    }
    const employee = this.selectedMenuEmployee;
    this.isSendingOnboarding = true;
    const sendingToast = this.toastrService.info(this.translate.instant('ON_BOARDING.sending'), undefined, {
      disableTimeOut: true,
      tapToDismiss: false,
      closeButton: false
    });
    this.workflowV2Service.startProcessInstance(`new_onboarding_cliReg-${this.cliRegIds[0]}`, {
      request: {
        contractId: this.contractId,
        cliEmpId: this.employeeId,
      },
      cliRegId: this.cliRegIds[0],
    }, false, ProfileMode.MSS).pipe(
      tap(() => {
        this.toastrService.success(this.translate.instant('ON_BOARDING.sent'));
        this.updateOnboardingStatus(employee, OnboardingStatus.WAITING_SUBMIT_FROM_EMPLOYEE);
      }),
      catchError(() => {
        this.toastrService.error(this.translate.instant('ON_BOARDING.send_error'));
        return of(null);
      }),
      finalize(() => {
        this.isSendingOnboarding = false;
        if (sendingToast) {
          this.toastrService.clear(sendingToast.toastId);
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  public get onboardingInProgress(): boolean {
    return this.isOnboardingInProgress(this.selectedMenuEmployee?.onboardingStatus);
  }

  public get onboardingItemClasses(): { [key: string]: boolean } {
    const inProgress = this.isSendingOnboarding || this.onboardingInProgress;
    return { 'disabled': inProgress, 'no-pointer': inProgress, 'pointer': !inProgress };
  }

  public get onboardingTooltip(): string {
    if (this.isSendingOnboarding) {
      return this.translate.instant('ON_BOARDING.sending');
    }
    return this.onboardingInProgress ? this.translate.instant('ONBOARDING.process_in_progress') : '';
  }

  public onSendOnboarding(): void {
    if (!this.onboardingInProgress) {
      this.sendOnboardingV2();
    }
  }

  public isOnboardingInProgress(onboardingStatus: OnboardingStatus): boolean {
    return onboardingStatus === OnboardingStatus.WAITING_SUBMIT_FROM_EMPLOYEE
      || onboardingStatus === OnboardingStatus.WAITING_CORRECTION_FROM_EMPLOYEE
      || onboardingStatus === OnboardingStatus.PENDING_VALIDATION;
  }

  private updateOnboardingStatus(employee: Data<CliEmpAttributes>, status: OnboardingStatus): void {
    if (!employee) {
      return;
    }
    employee.onboardingStatus = status;
    this.cd.detectChanges();
  }

  public openCreateTeamModal(newTeamMemberId?: number) {
    this.newTeamMemberId = newTeamMemberId;
    this.teamName.setValue('');
    this.teamName.markAsPristine();

    this.modalService.openModalWithId('create-team');
  }

  public createTeam() {
    let newTeamId: number ;
    this.teamsService.createTeam({name: this.teamName.value})
      .pipe(
        switchMap((team) => {
          newTeamId = team.id;
          return this.newTeamMemberId ? this.teamsService.addContractToTeam(newTeamId, this.newTeamMemberId) : of(team);
        }),
        tap(() => this.newTeamMemberId = null),
        switchMap(() => this.teamsService.getTeamsList()),
        tap(teams => this.teams = teams),
        switchMap(() => this.changeTeamObservable(newTeamId, '')),
        finalize(() => this.modalService.closeTopmostModal())
      )
      .subscribe();
  }

  public cancelCreateTeam(createPopup) {
    this.newTeamMemberId = null;
    createPopup.close();
  }

  public deleteTeam() {
    this.teamsService.deleteTeam(this.selectedTeam)
      .pipe(
        switchMap(() => this.teamsService.getTeamsList()),
        tap((teams) => this.teams = teams),
        switchMap(() => this.changeTeamObservable(0, this.searchTerm))
      )
      .subscribe();
  }

  public onMenuClick(data: { employee, event: MouseEvent }) {
    this.closeActiveMenu();
    this.computeMenuItems(data);

    this.activeMenu = this.viewContainer.createEmbeddedView(this.submenuTpl, {$implicit: data.employee});

    setTimeout(() => {
      const menuElement = this.activeMenu.rootNodes.filter(node => node && node.classList?.value.includes('submenu'))[0];
      this.positionMenu(menuElement, data.event, window.screen.availHeight);
    }, 0);
  }

  public addContractToTeam(contractId, team) {
    this.teamsService.addContractToTeam(team.id, contractId).pipe(
      switchMap(() => team.id === this.selectedTeam ? this.refreshTeamKeepingSelection() : of(null)),
      takeUntil(this.componentDestroyed$)
    )
      .subscribe();
  }

  public removeContractFromTeam(teamId, contractId) {
    this.teamsService.removeContractFromTeam(teamId, contractId).pipe(
      switchMap(() => this.refreshTeamKeepingSelection()),
      takeUntil(this.componentDestroyed$)
    )
      .subscribe();
  }

  private refreshTeamKeepingSelection(): Observable<Array<Data<CliEmpAttributes>>> {
    const selectedContractId = this.selectedEmployee?.empContracts?.[0]?.id;
    const wasListExpanded = this.listExpanded;
    return this.changeTeamObservable(this.selectedTeam, this.searchTerm).pipe(
      tap(() => this.restoreSelection(selectedContractId, wasListExpanded))
    );
  }

  private restoreSelection(contractId: number, wasListExpanded: boolean): void {
    const employee = contractId && this.employees.find(emp => emp.empContracts?.[0]?.id === contractId);
    if (employee) {
      this.onEmployeeClick({employee});
      this.listExpanded = wasListExpanded;
    }
  }

  public isEmployeeSelected(employee: Data<CliEmpAttributes>): boolean {
    return (this.selectedEmployee && employee === this.selectedEmployee);
  }

  private loadTeams(): void {

    this.isLoading = true;
    this.userService.getUser()
      .pipe(
        tap((user: User) => this.userId = user.cliEmpId),
        switchMap(() => this.teamsService.getTeamsList()),
        tap((teams) => {
          this.teams = teams;
          const lastViewedTeamId: string = localStorage.getItem('gp_last_viewed_team_id');
          this.selectedTeam = lastViewedTeamId ? parseInt(lastViewedTeamId, 10) : 0;
        }),
        switchMap(() => this.changeTeamObservable(this.selectedTeam, this.searchTerm))
      )
      .subscribe();
  }

  public backLinkClickEventHandler() {
    this.showDetailView = false;
    this.selectedEmployee = null;
    this.listExpanded = true;
  }

  private getDefaultTeam(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getDefaultSubordinates(searchTerm);
  }

  private getAllSubordinates(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getAllSubordinates(searchTerm);
  }

  private getIndirectSubordinates(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getIndirectSubordinates(searchTerm);
  }

  private getBackupSubordinates(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getBackupSubordinates(searchTerm);
  }

  private getUserTeam(teamId: number): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getTeamMembers(teamId, this.toggleSeeInactiveContracts);
  }

  private getFutureSubordinates(searchTerm: string): Observable<Array<Data<CliEmpAttributes>>> {
    return this.teamsService.getFutureSubordinates(searchTerm);
  }

  public customPlaceholderGenerator(contract: Contract) {
    const text: string = this.translate.instant('EMPLOYEE_FORM.Contract_name', {
      name: contract.cliRegNickName
    });
    return contract.flagUri
      ? `<img class="flag-icon" alt="flag-icon" src="${contract.flagUri}" /> ${text} ${this.getContractDate(contract)}`
      : `${text} ${this.getContractDate(contract)}`;
  }

  public getContractDate(contract: Contract): string {
    return `(${
      this.dateLocalizationPipe.transform(contract.begindate)
    } - ${
      contract.enddate === DATE_MAX || !contract.enddate
        ? '...'
        : this.dateLocalizationPipe.transform(contract.enddate)
    })`;
  }

  public selectContract(contract: Contract): void {
    this.selectedContract = contract;
  }

  public onEmployeeClick(data: { employee: Data<CliEmpAttributes> }) {
    this.selectedEmployee = data.employee;
    this.selectedEmployeeFullName = `${this.selectedEmployee.intLastName} ${this.selectedEmployee.intFirstName}`;
    this.selectedContract = this.contractDataService.getMainContract(data.employee.empContracts);
    this.contracts = this.selectedEmployee.empContracts.map(mapContract);
    this.showDetailView = true;
  }

  public select(employee: Data<CliEmpAttributes>): void {
    if (this.isEmployeeSelected(employee)) {
      this.collapseList();
      return;
    }
    this.onEmployeeClick({employee});
    this.listExpanded = this.isListPinned;
  }

  public onSearchFocus(): void {
    // For custom teams the input itself stays disabled (no searching),
    // but clicking the search should still re-open the employee list.
    if (!this.listExpanded) {
      this.listExpanded = true;
      this.cd.markForCheck();
    }
  }

  public toggleListPin(): void {
    if (this.isMobileViewport) {
      return;
    }
    this.isListPinned = !this.isListPinned;
    this.listExpanded = this.isListPinned || !this.selectedEmployee;
    this.cd.markForCheck();
  }

  public collapseList(): void {
    if (this.selectedEmployee && !this.isListPinned) {
      this.listExpanded = false;
      this.cd.markForCheck();
    }
  }

  public onTeamChange(team: number): void {
    this.listExpanded = true;
    this.searchTerm = '';
    this.searchTermControl.reset('', {emitEvent: false});
    this.changeTeam(team, '');
  }


  public getPicture(employee: AnyData): string {
    const link = employee?.links?.find(lnk => lnk.rel === 'photo');
    return link ? link.href : undefined;
  }

  public getEmployeeInitials(employee: any): string {
    return getInitials(employee?.firstName, employee?.lastName);
  }

  public isInactive(contracts: any): boolean {
    return !!contracts?.length && contracts[0].status === ContractStatus.INACTIVE;
  }

  public selectReportDefinition(reportDefinition: ReportDefinition): void {
    this.selectedReportDefinition = reportDefinition;
    this.hiddenValuesForReportDefinition.set('contractId', this.contractId);
    this.hiddenValuesForReportDefinition.set('employeeId', this.employeeId);
  }

  public reportDefinitionsLoaded(reportsFormData: ReportsFormData): void {
    this.reportDefinitions = reportsFormData.data;
    this.setReportDefinitionsAsMenuItems();
    if (!this.isDestroyed) {
      this.cd.detectChanges();
    }
  }

  public reportDefinitionListener(): void {
    this.selectedReportDefinition = null;
  }

  private setReportDefinitionsAsMenuItems() {
    if (!this.reportDefinitions || !this.menuItems) {
      return;
    }
    this.menuItems.communications = this.getReportDefinitionsAsMenuItemsByType(EMPLOYEE_COMMUNICATION);
  }

  public getReportDefinitionsAsMenuItemsByType(type: string): ReportDefinition[] {
    if (!this.reportDefinitions) {
      return [];
    }
    return this.reportDefinitions.filter((reportDefinition: ReportDefinition) =>
      reportDefinition.reportType === type && (!this.newOnboardingEnabled || reportDefinition.reportName !== 'OnboardingForm'));
  }

  private computeMenuItems(data: { employee; event: MouseEvent }): void {
    this.selectedMenuEmployee = data.employee;
    this.cliRegIds = data.employee.empContracts.map(empContract => empContract.cliRegID);
    if (data.employee.empContracts && data.employee.empContracts?.length) {
      this.contractId = data.employee.empContracts[0].id;
      this.employeeId = data.employee.id;
    }
  }

  private positionMenu(menuElement, positionEvent, screenHeight): void {
    if (!this.isMobileViewport) {
      menuElement.style.left = `${(positionEvent.pageX + 8)}px`;
    }

    if (this.showCompactMenu() && (positionEvent.pageY + menuElement.clientHeight) > screenHeight) {
      menuElement.style.top = `${positionEvent.pageY - menuElement.clientHeight}px`;
    } else {
      menuElement.style.top = `${positionEvent.pageY}px`;
    }
  }

  private showCompactMenu(): boolean {
    return this.isMobileViewport || this.isTabletViewport;
  }
}
